import { z } from 'zod'
import { createClient } from '@/shared/api/client'
import { validateResponseHasSchema } from '@/shared/api/validation'
import { YieldAction, YieldFileState, YieldFileType, YieldState } from '@/features/assets/constants'
import { buildUrl } from '@/shared/utils/urls'
import { TargetSystem } from '@/shared/constants'
import { roundToPrecision } from '@/helpers'
import { schemaForFieldWithState } from '@/shared/schemas'

const client = createClient(buildUrl(TargetSystem.AssetManagement))

export const fetchYield = (assetCode: string) => validateResponseHasSchema(
  client.sendGet(`assets/${assetCode}/yields`),
  z.object({
    data: z.object({
      canUpdate: z.boolean(),
      hasPendingUpdate: z.boolean(),
      designOutput: z.number().nullable(),
      projectedUsage: z.number().nullable(),
      state: z.nativeEnum(YieldState),
      guaranteePeriodEndDateActual: z.coerce.date().nullable(),
      guaranteePeriodEndDateExpected: z.coerce.date().nullable(),
      calculatedConsumptionPeriodYield: schemaForFieldWithState(z.number().nullable()),
      calculatedGuaranteePeriodYield: schemaForFieldWithState(z.number().nullable()),
      calculatedWarrantyPeriodYield: z.number().nullable(),
      systemSize: z.number(),
      files: z.array(
        z.object({
          type: z.nativeEnum(YieldFileType),
          uploadId: z.string(),
          state: z.nativeEnum(YieldFileState),
          stateReason: z.string().nullable(),
        })
      ),
      approvals: z.object({
        requiredCount: z.number(),
        allowedUsers: z.array(z.string()),
        received: z.array(
          z.object({
            userId: z.string(),
            action: z.nativeEnum(YieldAction),
            actionedAt: z.coerce.date(),
          })
        )
      }),
      monthlyEnergy: z.array(
        z.object({
          month: z.number().int().gte(1).lte(12),
          expectedConsumptionPeriod: z.number(),
          expectedGuaranteePeriod: z.number(),
          actual: z.number().nullable(),
        })
      ),
    })
  })
).then(
  ({ data }) => {
    const { calculatedConsumptionPeriodYield, calculatedGuaranteePeriodYield } = data

    const updated = {
      ...data,
      phase: extractPhase(data.state),
      status: extractStatus(data.state),
      calculatedWarrantyPeriodYield: roundToPrecision(data.calculatedWarrantyPeriodYield, 2)
    }

    if (calculatedConsumptionPeriodYield) {
      updated.calculatedConsumptionPeriodYield = {
        ...calculatedConsumptionPeriodYield,
        value: roundToPrecision(calculatedConsumptionPeriodYield.value, 2)
      }
    }

    if (calculatedGuaranteePeriodYield) {
      updated.calculatedGuaranteePeriodYield = {
        ...calculatedGuaranteePeriodYield,
        value: roundToPrecision(calculatedGuaranteePeriodYield.value, 2)
      }
    }

    return updated
  }
)

function extractPhase(state: YieldState): 'guarantee'|'warranty'|'initial' {
  switch (state) {
    case YieldState.GuaranteeApproved:
    case YieldState.GuaranteeApproving:
    case YieldState.GuaranteeRejected:
      return 'guarantee'
    case YieldState.WarrantyApproved:
      return 'warranty'
    default:
      return 'initial'
  }
}

function extractStatus(state: YieldState): 'approved'|'rejected'|'approving'|undefined {
  switch (state) {
    case YieldState.GuaranteeApproved:
    case YieldState.WarrantyApproved:
      return 'approved'
    case YieldState.InitialRejected:
    case YieldState.GuaranteeRejected:
      return 'rejected'
    case YieldState.InitialApproving:
    case YieldState.GuaranteeApproving:
      return 'approving'
    default:
      return undefined
  }
}
